<script setup>
import { useI18n } from 'vue-i18n';
import { createRealEstateBrokerDashboardPresenter } from '../composables/realEstateBrokerDashboardPresenter';

defineProps({
  row: { type: Object, required: true },
});

const emit = defineEmits(['open']);
const { t } = useI18n();
const {
  money,
  statusLabel,
  statusClass,
  dateLabel,
  dateValue,
  address,
  addressDetail,
  propertyImage,
  installmentLabel,
} = createRealEstateBrokerDashboardPresenter(t);
const dateClass = row =>
  ({
    em_andamento: 'text-[#D69500]',
    liberado: 'text-[#6B9539]',
    solicitado: 'text-[#01988C]',
    em_transferencia: 'text-[#6B9539]',
    concluido: 'text-[#6B9539]',
    cancelado: 'text-[#E33838]',
  })[row?.status?.grupo] || 'text-[#464F53]';
</script>

<template>
  <article
    :data-testid="`broker-recent-row-${row.commission_split_id}`"
    class="grid cursor-pointer gap-6 border-b border-[#E0E7EB] bg-white px-6 py-[22px] shadow-[0_0_6px_0_rgba(0,0,0,0.02),0_2px_4px_0_rgba(0,0,0,0.08)] hover:bg-lais-bg-soft/60 md:grid-cols-[max-content_max-content_minmax(0,1fr)_max-content] md:items-center md:gap-4 md:p-4 md:shadow-none"
    tabindex="0"
    @click="emit('open', row.parcela_id)"
    @keydown.enter="emit('open', row.parcela_id)"
  >
    <div class="flex items-start justify-between gap-4 md:contents">
      <div class="flex min-w-0 flex-col gap-4 md:contents">
        <span
          class="w-fit rounded-full px-2 py-1 text-xs font-semibold"
          :class="statusClass(row)"
          data-testid="broker-recent-status"
        >
          {{ statusLabel(row) }}
        </span>
        <div class="space-y-1" data-testid="broker-recent-value">
          <p class="text-xl font-bold text-[#6B9539]">{{ money(row) }}</p>
          <p class="text-xs text-[#74848B]">{{ installmentLabel(row) }}</p>
        </div>
      </div>
      <div class="shrink-0 text-right md:order-4">
        <p class="text-sm" :class="dateClass(row)">{{ dateLabel(row) }}</p>
        <p class="text-xs text-[#74848B]">{{ dateValue(row) }}</p>
      </div>
    </div>
    <div class="flex min-w-0 items-center gap-2 md:order-3">
      <img
        v-if="propertyImage(row)"
        :src="propertyImage(row)"
        :alt="t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.PROPERTY_IMAGE')"
        class="size-10 shrink-0 rounded-full object-cover"
      />
      <span
        v-else
        class="i-lucide-image grid size-10 shrink-0 place-items-center rounded-full bg-lais-bg-soft text-lais-text-muted"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <p class="truncate text-base text-[#5D696F]">{{ address(row) }}</p>
        <p v-if="addressDetail(row)" class="truncate text-sm text-[#74848B]">
          {{ addressDetail(row) }}
        </p>
      </div>
    </div>
  </article>
</template>
